import { css, cva } from '@/styled-system/css';
import { styled } from '@/styled-system/jsx';

import { Table } from '@zillow/constellation-10';

import { stickyLeftCellRecipe, tableCellHoverStyles } from '../../../styles/tableFixedScroll';

const TABLE_CELL_BORDER = '1px solid {colors.Table.Cell.border}';

export const PROPERTY_ROW_WIDTHS = {
  FIRST_CELL: '80px',
  MIDDLE_CELL: '50px',
  LAST_CELL: '20px',
} as const;

/** multiplex expand/collapse chevron column: Icon `size="sm"` plus padding (`spacing.800`). */
const CHEVRON_GUTTER = '800' as const;

const IMAGE_DIMENSIONS = {
  WIDTH: 100, // max-width of image
  HEIGHT: 60, // max-height of image
};

export const CELL_DIMENSIONS = {
  WIDTH: 200, // max-width of cell
  HEIGHT: 63, // min-height of cell
};

const hiddenTextStyles = {
  /* bypass the need for text to properly align */
  '&:before': {
    // eslint-disable-next-line curly-quotes/no-straight-quotes
    content: '"hidden"',
    display: 'flex',
    height: '0px',
    fontSize: '0px',
  },
} as const;

export const propertyPhotoContainerStyles = css.raw({
  display: { base: 'none', sm: 'flex' },
  marginRight: '300',
  ...hiddenTextStyles,
});

export const propertyPhotoRecipe = cva({
  base: {
    margin: '0 auto',
    borderRadius: '200',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center center',
  },
  variants: {
    hasPhoto: {
      true: {
        backgroundSize: 'cover',
      },
      false: {
        backgroundSize: 'contain',
      },
    },
    isParentPropertyRow: {
      true: {
        width: '100%',
        height: '100%',
      },
      false: {
        width: `${IMAGE_DIMENSIONS.WIDTH}px`,
        height: `${IMAGE_DIMENSIONS.HEIGHT}px`,
      },
    },
  },
  defaultVariants: {
    hasPhoto: false,
    isParentPropertyRow: false,
  },
});

export const cellAddressContainer = css.raw({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'stretch',
  minWidth: 0,
  width: '100%',
});

export const chevronContainerStyles = css.raw({
  display: 'flex',
  flexDirection: 'row',
  flexGrow: 0,
  flexShrink: 0,
  alignItems: 'center',
  justifyContent: 'center',
  width: CHEVRON_GUTTER,
  minWidth: CHEVRON_GUTTER,
  maxWidth: CHEVRON_GUTTER,
});

// address and summary container parent container
export const flexContainerStyles = css.raw({
  display: 'flex',
  minHeight: `${CELL_DIMENSIONS.HEIGHT}px`,
  alignItems: 'center',
  ...hiddenTextStyles,
});

export const propertyAddressStyles = css.raw({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  maxWidth: '100%',
  '& > *': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

export const TableHeaderCell = styled(Table.HeaderCell, {
  base: {
    fontSize: 'body-sm',
    fontWeight: 'bold',
    padding: '{spacing.200}',
    display: { base: 'none', lg: 'table-cell' },
    width: PROPERTY_ROW_WIDTHS.MIDDLE_CELL,

    '&:first-child': {
      paddingLeft: '{spacing.600}',
      width: PROPERTY_ROW_WIDTHS.FIRST_CELL,
      display: 'table-cell',
    },

    '&:last-child': {
      width: PROPERTY_ROW_WIDTHS.LAST_CELL,
      display: 'table-cell',
    },

    md: {
      width: PROPERTY_ROW_WIDTHS.MIDDLE_CELL,
    },
  },
});

export const TableCell = styled(Table.Cell, {
  base: {
    height: '105px',

    // Target cells in the last row (within the last anchor wrapper)
    'tbody > a:last-child tr > &': {
      borderBottom: 'none',
    },

    '&:first-child': {
      width: 'auto',
      maxWidth: 'none',
    },

    '&:last-child': {
      width: '60px',
      minWidth: '60px',
      maxWidth: '60px',
    },

    md: {
      padding: '{spacing.400} 0 {spacing.400} {spacing.400}',
      '&:last-child': {
        paddingRight: '400',
      },
    },

    lg: {
      paddingLeft: '200',
      width: PROPERTY_ROW_WIDTHS.MIDDLE_CELL,
      '&:first-child': {
        // paddingLeft: '600',
        paddingLeft: '0',

        width: PROPERTY_ROW_WIDTHS.FIRST_CELL,
      },
      '&:last-child': {
        paddingRight: '200',
        width: PROPERTY_ROW_WIDTHS.LAST_CELL,
      },
    },
  },
});

export const tagStyles = css.raw({
  display: 'flex',
  width: 'fit-content',
  textWrap: 'wrap',
});

/** Nested unit rows (not the multiplex parent) use a subtle background on table cells. */
export const groupUnitChildRowCellBackgroundCss = css.raw({
  backgroundColor: '{colors.bg.softest}',
});

/** Nested unit rows: no table cell padding (inset comes from inner layout). */
export const groupUnitChildRowTableCellPaddingCss = css.raw({
  height: '56px',
  padding: '0',
  md: {
    padding: '0',
    '&:last-child': {
      paddingRight: '0',
    },
  },
  lg: {
    padding: '0',
    paddingLeft: '0',
    '&:first-child': {
      paddingLeft: '0',
    },
    '&:last-child': {
      paddingRight: '0',
    },
  },
});

export const cellAnchorStyles = css.raw({
  color: 'text.neutral',
  textDecoration: 'none',
  'td:hover &': {
    color: 'blue.500',
    textDecoration: 'underline',
  },
});

// Re-export shared styles for backward compatibility
export { stickyLeftCellRecipe, tableCellHoverStyles };

export const leftDataCellStyles = (isScrolledToLeft: boolean) =>
  css.raw({
    ...stickyLeftCellRecipe.raw({ scrolled: isScrolledToLeft }),
    ...tableCellHoverStyles,
    verticalAlign: 'middle',
    overflow: 'hidden', // Prevent cell content from overflowing

    // Bottom row is always has border
    'tbody > *:last-child tr:last-of-type &': {
      borderBottom: TABLE_CELL_BORDER,
    },
  });

export const rightDataCellStyles = css.raw({
  ...tableCellHoverStyles,

  // Bottom row is always has border
  'tbody > *:last-child tr:last-of-type &': {
    borderBottom: TABLE_CELL_BORDER,
  },
});

export const HiddenMobileTableCell = styled(Table.Cell, {
  base: {
    display: { base: 'none', lg: 'table-cell' },
    width: PROPERTY_ROW_WIDTHS.MIDDLE_CELL,
    ...tableCellHoverStyles,
  },
});
